import type React from "react"
import { useState, useEffect } from "react"
import {
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Box,
  Alert,
} from "@mui/material"
import { CloudUpload, CheckCircle, Schedule, Description } from "@mui/icons-material"
import FileUploader from "./FileUploader"
// import { getRequiredDocuments, uploadDocument, getUploadedDocuments } from "../../Services/OnboardingService2"
import styles from "./OnboardingDocuments.module.css"
import { getRequiredDocuments, getUploadedDocuments, uploadDocument } from "../../api/OnboardingService2"

interface OnboardingDocumentsProps {
  employeeId: string
  isCompleted: boolean
  onComplete: () => void
}

const OnboardingDocuments: React.FC<OnboardingDocumentsProps> = ({ employeeId, isCompleted, onComplete }) => {
  const [requiredDocuments, setRequiredDocuments] = useState<any[]>([])
  const [uploadedDocuments, setUploadedDocuments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  console.log("OnboardingDocuments rendered for employeeId:", employeeId)
const busId = "bus_id_b96c58e8adabb47c000dc8dc5c02900f"
  const branchId = "bra_id_b96c58e8adabb47c038dc8dc5c00009m"
  useEffect(() => {
    fetchDocuments()
  }, [employeeId])

  useEffect(() => {
  if (!isCompleted && requiredDocuments.length > 0) {
    const allApproved = requiredDocuments.every((doc) => {
      const uploaded = uploadedDocuments.find((u) => u.document_id === doc.document_id);
      return uploaded?.status === "approved";
    });

    if (allApproved) {
      onComplete();
    }
  }
}, [uploadedDocuments, requiredDocuments, isCompleted, onComplete]);


  const fetchDocuments = async () => {
    try {
      setLoading(true)
      const [required, uploaded] = await Promise.all([getRequiredDocuments(employeeId), getUploadedDocuments(employeeId, busId, branchId)])
      console.log("Fetched documents:", { required, uploaded })
      setRequiredDocuments(required)
      setUploadedDocuments(uploaded)
    } catch (err) {
      setError("Failed to load documents")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileUpload = async (documentId: string, files: File[], onboardingDocumentId: string) => {
    if (files.length === 0) return

    // console.log("Uploading file for documentId:", documentId, files[0],files)

    try {
      setUploading(documentId)
      setError(null)
      

      const file = files[0]
      await uploadDocument(employeeId, documentId, file, onboardingDocumentId)

      // Refresh uploaded documents
      await fetchDocuments()

      // Check if all documents are uploaded
      // const allUploaded = requiredDocuments.every((doc) =>
      //   uploadedDocuments.some((uploaded) => uploaded.document_id === doc.document_id),
      // )

      // if (allUploaded && !isCompleted) {
      //   onComplete()
      // }
    } catch (err) {
      setError("Failed to upload document")
      console.error(err)
    } finally {
      setUploading(null)
    }
  }


  
  const isDocumentUploaded = (documentId: string) => {
    return uploadedDocuments.some((doc) => doc.documentId === documentId)
  }

  const getDocumentStatus = (documentId: string) => {
    const uploaded = uploadedDocuments.find((doc) => doc.document_id === documentId)
    if (uploaded) {
      return uploaded.status || "uploaded"
    }
    return "pending"
  }

  const getStatusChip = (status: string) => {
    switch (status) {
      case "approved":
        return <Chip label="Approved" color="success" size="small" />
      case "rejected":
        return <Chip label="Rejected" color="error" size="small" />
      case "uploaded":
        return <Chip label="Under Review" color="warning" size="small" />
      default:
        return <Chip label="Pending" color="default" size="small" />
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading required documents...</Typography>
        </CardContent>
      </Card>
    )
  }

  const completedCount = requiredDocuments.filter((doc) => getDocumentStatus(doc.document_id) === "approved").length

  return (
    <div className={styles.container}>
      <Card className={styles.summaryCard}>
        <CardContent>
          <Typography variant="h6" className={styles.summaryTitle}>
            Document Upload Progress
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {completedCount} of {requiredDocuments.length} documents approved
          </Typography>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" className={styles.error}>
          {error}
        </Alert>
      )}

      {isCompleted && (
        <Alert severity="success" className={styles.success}>
          All required documents have been uploaded and approved!
        </Alert>
      )}

      <List className={styles.documentList}>
        {requiredDocuments.map((document) => {
          const isUploaded = isDocumentUploaded(document.document_id)
          const status = getDocumentStatus(document.document_id)
          const isCurrentlyUploading = uploading === document.document_id

          return (
            <ListItem key={document.document_id} className={styles.documentItem}>
              <ListItemIcon>
                {status === "approved" ? (
                  <CheckCircle color="success" />
                ) : isUploaded ? (
                  <Schedule color="warning" />
                ) : (
                  <Description color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText primary={document.document_name} secondary={document.message} className={styles.documenttext} />
              <ListItemSecondaryAction className={styles.documentActions}>
                <Box className={styles.statusContainer}>
                  {getStatusChip(status)}
                  {!isUploaded || status === "rejected" ? (
                    <Box className={styles.uploadContainer}>
                      <FileUploader
                        name={`document-${document.document_id}`}
                        accept={document.acceptedFormats || ".pdf,.doc,.docx,.jpg,.png"}
                        maxSize={5}
                        multiple={false}
                        description={`Upload ${document.document_name}`}
                        onFilesChange={(files) => handleFileUpload(document.document_id, files, document.onboarding_document_id)}
                        disabled={isCurrentlyUploading}
                      />
                      {isCurrentlyUploading && (
                        <Typography variant="caption" color="primary">
                          Uploading...
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUpload />}
                      onClick={() => {
                        // Handle re-upload if needed
                      }}
                    >
                      Re-upload
                    </Button>
                  )}
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
          )
        })}
      </List>
    </div>
  )
}

export default OnboardingDocuments
